'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { locales } from '@/i18n/locales';

// Optionally locale-prefixed /c/<slug> or /categories, optionally followed by
// the /page/N pagination segment or the /search twin segment. Mirrors the
// route shapes handled by `withListingRewrite` on the server.
const LISTING_ROUTE_RE = new RegExp(
  `^(?:/(?:${locales.join('|')}))?(?:/c/[^/]+|/categories)(?=/page/\\d+/?$|/search/?$|/?$)`,
);

/**
 * Builds hrefs for client-side listing navigations (sort, filters).
 *
 * On category listing routes the returned href targets the dynamic twin
 * (`<base>/search?params`) when listing params are present, and the clean
 * static base path when none remain. Navigating the twin's own pathname is
 * what makes these interactions reliable: the base path (and its /page/N
 * variants) is statically generated, and the Next.js client cache reuses a
 * cached static payload for any search params on the same pathname — a
 * `router.replace('<base>?params')` can therefore render the unfiltered
 * static page. The server-side rewrite in `withListingRewrite` still serves
 * clean `<base>?params` URLs on direct loads.
 *
 * On every other route (e.g. /search, /brands/*) the href keeps the current
 * pathname — those routes are dynamic and render their own search params.
 */
export function useListingNavigation(): {
  buildListingHref: (params: URLSearchParams) => string;
} {
  const pathname = usePathname() || '/';

  const buildListingHref = useCallback(
    (params: URLSearchParams) => {
      const qs = params.toString();
      const listingBase = pathname.match(LISTING_ROUTE_RE)?.[0];
      if (listingBase) {
        return qs ? `${listingBase}/search?${qs}` : listingBase;
      }
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname],
  );

  return { buildListingHref };
}
